import { useEffect, useState } from "react";
import { Card, Col, Row, Table, Badge } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Translate } from "../../../../Enums/Tranlate";
import SalariesService from "../../../../../services/HRM/SalariesService";
import Loader from "../../../../common/Loader";

const months = [
	"january",
	"february",
	"march",
	"april",
	"may",
	"june",
	"july",
	"august",
	"september",
	"october",
	"november",
	"december",
];

const PayrollDetails = () => {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const lang = useSelector((state) => state.auth?.lang);
	const navigate = useNavigate();
	const { employeeId } = useParams();
	const location = useLocation();
	const salariesService = new SalariesService();

	const searchParams = new URLSearchParams(location.search);
	const month = searchParams.get("month");
	const year = searchParams.get("year");

	useEffect(() => {
		if (!employeeId || !month || !year) return;
		setLoading(true);
		salariesService
			.getPayrollDetails(employeeId, { month, year })
			.then((res) => {
				if (res?.status === 200) {
					setData(res?.data?.data);
				}
			})
			.finally(() => setLoading(false));
	}, [employeeId, month, year]);

	const formatAmount = (val) =>
		val != null ? `${Number(val).toFixed(3)} ${Translate[lang]?.kwd}` : "-";

	const formatDate = (dateStr) => {
		if (!dateStr) return "-";
		return new Date(dateStr).toLocaleDateString(
			lang === "ar" ? "ar-EG" : "en-GB",
			{ day: "2-digit", month: "short", year: "numeric" },
		);
	};

	const getTreatmentLabel = (treatment) => {
		const map = {
			PAID_WITHIN_ALLOWANCE: Translate[lang]?.paid_within_allowance,
			UNPAID_FULLY_DEDUCTED: Translate[lang]?.unpaid_fully_deducted,
			WITHIN_ALLOWANCE: Translate[lang]?.within_allowance,
		};
		return map[treatment] || treatment;
	};

	const getTreatmentVariant = (treatment) => {
		if (
			treatment === "PAID_WITHIN_ALLOWANCE" ||
			treatment === "WITHIN_ALLOWANCE"
		)
			return "success";
		if (treatment === "UNPAID_FULLY_DEDUCTED") return "danger";
		return "secondary";
	};

	const deductionTypeLabel = (type) => {
		const map = {
			MANUAL_MONEY: Translate[lang]?.deduction,
			MANUAL_DAYS: Translate[lang]?.deduction,
			LOAN_INSTALLMENT: Translate[lang]?.loan_installment,
			UNPAID_VACATION: Translate[lang]?.unpaid_vacation,
			ABSENCE: Translate[lang]?.absent,
		};
		return map[type] || type;
	};

	const deductionVariant = (type) => {
		if (type === "LOAN_INSTALLMENT") return "warning";
		if (type === "UNPAID_VACATION" || type === "ABSENCE") return "danger";
		return "secondary";
	};

	if (loading) {
		return (
			<div style={{ height: "400px" }}>
				<Loader />
			</div>
		);
	}

	if (!data) return null;

	const monthLabel =
		Translate[lang]?.[months[data.month - 1]] || months[data.month - 1];

	return (
		<div className="payroll-details">
			{/* Header */}
			<div
				className="d-flex align-items-center mb-4 gap-3"
				style={{ gap: "12px" }}>
				<button
					className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
					onClick={() => navigate(-1)}>
					<i className={`la la-arrow-${lang === "ar" ? "right" : "left"}`} />
					{Translate[lang]?.back}
				</button>
				<h4 className="mb-0">{Translate[lang]?.payroll_details}</h4>
			</div>

			{/* Employee Info Card */}
			<Card className="mb-3 shadow-sm">
				<Card.Body>
					<Row className="align-items-center">
						<Col md={1} xs={2} className="text-center">
							<div
								style={{
									width: 52,
									height: 52,
									borderRadius: "50%",
									background: "var(--primary, #6571ff)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									color: "#fff",
									fontSize: 22,
									fontWeight: 700,
								}}>
								{data.employeeName?.charAt(0)?.toUpperCase()}
							</div>
						</Col>
						<Col md={5} xs={10}>
							<h5 className="mb-0 fw-bold">{data.employeeName}</h5>
							<p className="mb-0 text-muted small">
								{data.jobTitle} — {data.department}
							</p>
							<p className="mb-0 text-muted small">{data.employeeId}</p>
						</Col>
						<Col md={6} xs={12} className="mt-3 mt-md-0">
							<Row className="text-center">
								<Col xs={4}>
									<div className="text-muted small">
										{Translate[lang]?.month}
									</div>
									<div className="fw-semibold">{monthLabel}</div>
								</Col>
								<Col xs={4}>
									<div className="text-muted small">
										{Translate[lang]?.year}
									</div>
									<div className="fw-semibold">{data.year}</div>
								</Col>
								<Col xs={4}>
									<div className="text-muted small">
										{Translate[lang]?.daily_rate}
									</div>
									<div className="fw-semibold">
										{formatAmount(data.dailyRate)}
									</div>
								</Col>
							</Row>
						</Col>
					</Row>
				</Card.Body>
			</Card>

			{/* Summary Cards Row */}
			<Row className="mb-3 g-3">
				<Col md={3} sm={6}>
					<Card
						className="h-100 shadow-sm border-0"
						style={{ borderLeft: "4px solid #6571ff", borderRadius: 8 }}>
						<Card.Body className="py-3">
							<p className="text-muted small mb-1">{Translate[lang]?.salary}</p>
							<h5 className="mb-0 fw-bold text-primary">
								{formatAmount(data.baseSalary)}
							</h5>
							<p className="text-muted small mb-0 mt-1">
								{Translate[lang]?.basic_salary}
							</p>
						</Card.Body>
					</Card>
				</Col>
				<Col md={3} sm={6}>
					<Card
						className="h-100 shadow-sm border-0"
						style={{ borderLeft: "4px solid #dc3545", borderRadius: 8 }}>
						<Card.Body className="py-3">
							<p className="text-muted small mb-1">
								{Translate[lang]?.total_deductions}
							</p>
							<h5 className="mb-0 fw-bold text-danger">
								{formatAmount(data.totalDeductions)}
							</h5>
							<p className="text-muted small mb-0 mt-1">&nbsp;</p>
						</Card.Body>
					</Card>
				</Col>
				<Col md={3} sm={6}>
					<Card
						className="h-100 shadow-sm border-0"
						style={{ borderLeft: "4px solid #28a745", borderRadius: 8 }}>
						<Card.Body className="py-3">
							<p className="text-muted small mb-1">
								{Translate[lang]?.final_payroll_salary}
							</p>
							<h5 className="mb-0 fw-bold text-success">
								{formatAmount(data.finalPayrollSalary)}
							</h5>
							{data.isManuallyOverridden && (
								<Badge bg="warning" text="dark" className="mt-1 small">
									{Translate[lang]?.manually_overridden}
								</Badge>
							)}
						</Card.Body>
					</Card>
				</Col>
				<Col md={3} sm={6}>
					<Card
						className="h-100 shadow-sm border-0"
						style={{ borderLeft: "4px solid #17a2b8", borderRadius: 8 }}>
						<Card.Body className="py-3">
							<p className="text-muted small mb-1">
								{Translate[lang]?.calculated_payroll_salary}
							</p>
							<h5 className="mb-0 fw-bold" style={{ color: "#17a2b8" }}>
								{formatAmount(data.calculatedPayrollSalary)}
							</h5>
							<p className="text-muted small mb-0 mt-1">&nbsp;</p>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<Row className="g-3 mb-3">
				{/* Attendance Summary */}
				<Col md={6}>
					<Card className="h-100 shadow-sm">
						<Card.Header className="bg-white fw-semibold border-bottom">
							<i className="la la-calendar-check me-2 text-primary" />
							{Translate[lang]?.attendance_summary}
						</Card.Header>
						<Card.Body className="p-0">
							<Table className="mb-0" borderless responsive>
								<tbody>
									<tr>
										<td className="text-muted ps-3">
											{Translate[lang]?.working_days_in_month}
										</td>
										<td className="fw-semibold text-end pe-3">
											{data.attendanceSummary?.workingDaysInMonth}
										</td>
									</tr>
									<tr style={{ background: "#f8f9fa" }}>
										<td className="text-muted ps-3">
											{Translate[lang]?.attended_days}
										</td>
										<td className="fw-semibold text-end pe-3 text-success">
											{data.attendanceSummary?.attendedDays}
										</td>
									</tr>
									<tr>
										<td className="text-muted ps-3">
											{Translate[lang]?.approved_leave_days}
										</td>
										<td className="fw-semibold text-end pe-3 text-info">
											{data.attendanceSummary?.approvedLeaveDays}
										</td>
									</tr>
									<tr style={{ background: "#f8f9fa" }}>
										<td className="text-muted ps-3">
											{Translate[lang]?.absent_days}
										</td>
										<td className="fw-semibold text-end pe-3 text-danger">
											{data.attendanceSummary?.absentDays}
										</td>
									</tr>
								</tbody>
							</Table>
						</Card.Body>
					</Card>
				</Col>

				{/* Sick Leave Summary */}
				<Col md={6}>
					<Card className="h-100 shadow-sm">
						<Card.Header className="bg-white fw-semibold border-bottom">
							<i className="la la-medkit me-2 text-danger" />
							{Translate[lang]?.sick_leave_summary}
						</Card.Header>
						<Card.Body className="p-0">
							<Table className="mb-0" borderless responsive>
								<tbody>
									<tr>
										<td className="text-muted ps-3">
											{Translate[lang]?.annual_allowance_days}
										</td>
										<td className="fw-semibold text-end pe-3">
											{data.sickLeaveSummary?.annualAllowanceDays}
										</td>
									</tr>
									<tr style={{ background: "#f8f9fa" }}>
										<td className="text-muted ps-3">
											{Translate[lang]?.used_days_this_year}
										</td>
										<td className="fw-semibold text-end pe-3">
											{data.sickLeaveSummary?.usedDaysThisYear}
										</td>
									</tr>
									<tr>
										<td className="text-muted ps-3">
											{Translate[lang]?.remaining_annual_days}
										</td>
										<td className="fw-semibold text-end pe-3 text-success">
											{data.sickLeaveSummary?.remainingAnnualDays}
										</td>
									</tr>
									<tr style={{ background: "#f8f9fa" }}>
										<td className="text-muted ps-3">
											{Translate[lang]?.taken_days_this_month}
										</td>
										<td className="fw-semibold text-end pe-3">
											{data.sickLeaveSummary?.takenDaysThisMonth}
										</td>
									</tr>
									<tr>
										<td className="text-muted ps-3">
											{Translate[lang]?.excess_days}
										</td>
										<td className="fw-semibold text-end pe-3 text-danger">
											{data.sickLeaveSummary?.excessDays}
										</td>
									</tr>
									<tr style={{ background: "#f8f9fa" }}>
										<td className="text-muted ps-3">
											{Translate[lang]?.deducted_amount}
										</td>
										<td className="fw-semibold text-end pe-3 text-danger">
											{formatAmount(data.sickLeaveSummary?.deductedAmount)}
										</td>
									</tr>
								</tbody>
							</Table>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<Row className="g-3 mb-3">
				{/* Vacation Summary */}
				<Col md={6}>
					<Card className="shadow-sm">
						<Card.Header className="bg-white fw-semibold border-bottom">
							<i className="la la-plane me-2 text-info" />
							{Translate[lang]?.vacation_summary}
						</Card.Header>
						<Card.Body className="p-0">
							<Table className="mb-0" borderless responsive>
								<tbody>
									<tr>
										<td className="text-muted ps-3">
											{Translate[lang]?.monthly_allowance_days}
										</td>
										<td className="fw-semibold text-end pe-3">
											{data.vacationSummary?.monthlyAllowanceDays}
										</td>
									</tr>
									<tr style={{ background: "#f8f9fa" }}>
										<td className="text-muted ps-3">
											{Translate[lang]?.taken_days_this_month}
										</td>
										<td className="fw-semibold text-end pe-3">
											{data.vacationSummary?.takenDaysThisMonth}
										</td>
									</tr>
									<tr>
										<td className="text-muted ps-3">
											{Translate[lang]?.unpaid_days}
										</td>
										<td className="fw-semibold text-end pe-3 text-danger">
											{data.vacationSummary?.unpaidDays}
										</td>
									</tr>
									<tr style={{ background: "#f8f9fa" }}>
										<td className="text-muted ps-3">
											{Translate[lang]?.deducted_amount}
										</td>
										<td className="fw-semibold text-end pe-3 text-danger">
											{formatAmount(data.vacationSummary?.deductedAmount)}
										</td>
									</tr>
								</tbody>
							</Table>
						</Card.Body>
						{data.vacationSummary?.breakdown?.length > 0 && (
							<>
								<div className="px-3 pt-2 pb-1 text-muted small fw-semibold border-top">
									{Translate[lang]?.deductions_breakdown}
								</div>
								<Table responsive size="sm" className="mb-0">
									<thead>
										<tr
											className="text-center"
											style={{ background: "#f8f9fa" }}>
											<th className="small fw-semibold">
												{Translate[lang]?.departure_day}
											</th>
											<th className="small fw-semibold">
												{Translate[lang]?.return_day}
											</th>
											<th className="small fw-semibold">
												{Translate[lang]?.type}
											</th>
											<th className="small fw-semibold">
												{Translate[lang]?.number_of_days}
											</th>
											<th className="small fw-semibold">
												{Translate[lang]?.treatment}
											</th>
											<th className="small fw-semibold">
												{Translate[lang]?.deducted_amount}
											</th>
										</tr>
									</thead>
									<tbody>
										{data.vacationSummary.breakdown.map((v, i) => (
											<tr key={i} className="text-center">
												<td className="small">{formatDate(v.departureDay)}</td>
												<td className="small">{formatDate(v.returnDay)}</td>
												<td>
													<Badge
														bg={v.type === "PAID" ? "success" : "danger"}
														className="small">
														{v.type === "PAID"
															? Translate[lang]?.paid
															: Translate[lang]?.not_paid}
													</Badge>
												</td>
												<td className="small">{v.numberOfDays}</td>
												<td>
													<Badge
														bg={getTreatmentVariant(v.treatment)}
														className="small">
														{getTreatmentLabel(v.treatment)}
													</Badge>
												</td>
												<td className="small text-danger">
													{formatAmount(v.deductedAmount)}
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							</>
						)}
					</Card>
				</Col>

				{/* Loan Summary */}
				<Col md={6}>
					<Card className="shadow-sm">
						<Card.Header className="bg-white fw-semibold border-bottom">
							<i className="la la-money me-2 text-warning" />
							{Translate[lang]?.loan_summary}
						</Card.Header>
						<Card.Body className="p-0">
							<Table className="mb-0" borderless responsive>
								<tbody>
									<tr>
										<td className="text-muted ps-3">
											{Translate[lang]?.active_loans_count}
										</td>
										<td className="fw-semibold text-end pe-3">
											{data.loanSummary?.activeLoansCount}
										</td>
									</tr>
									<tr style={{ background: "#f8f9fa" }}>
										<td className="text-muted ps-3">
											{Translate[lang]?.total_deducted_amount}
										</td>
										<td className="fw-semibold text-end pe-3 text-danger">
											{formatAmount(data.loanSummary?.totalDeductedAmount)}
										</td>
									</tr>
								</tbody>
							</Table>
						</Card.Body>
						{data.loanSummary?.breakdown?.length > 0 && (
							<>
								<div className="px-3 pt-2 pb-1 text-muted small fw-semibold border-top">
									{Translate[lang]?.deductions_breakdown}
								</div>
								<Table responsive size="sm" className="mb-0">
									<thead>
										<tr
											className="text-center"
											style={{ background: "#f8f9fa" }}>
											<th className="small fw-semibold">
												{Translate[lang]?.purpose}
											</th>
											<th className="small fw-semibold">
												{Translate[lang]?.installment_number}
											</th>
											<th className="small fw-semibold">
												{Translate[lang]?.total_loan_amount}
											</th>
											<th className="small fw-semibold">
												{Translate[lang]?.installment_amount}
											</th>
										</tr>
									</thead>
									<tbody>
										{data.loanSummary.breakdown.map((l, i) => (
											<tr key={i} className="text-center">
												<td className="small">{l.purpose}</td>
												<td className="small">
													{l.installmentNumber} / {l.totalInstallments}
												</td>
												<td className="small">
													{formatAmount(l.totalLoanAmount)}
												</td>
												<td className="small text-danger">
													{formatAmount(l.installmentAmount)}
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							</>
						)}
					</Card>
				</Col>
			</Row>

			{/* Manual Deduction Summary */}
			{data.manualDeductionSummary?.count > 0 && (
				<Card className="mb-3 shadow-sm">
					<Card.Header className="bg-white fw-semibold border-bottom">
						<i className="la la-edit me-2 text-secondary" />
						{Translate[lang]?.manual_deduction_summary}
					</Card.Header>
					<Card.Body className="p-0">
						<Table responsive className="mb-0">
							<thead>
								<tr className="text-center" style={{ background: "#f8f9fa" }}>
									<th className="small fw-semibold">{Translate[lang]?.type}</th>
									<th className="small fw-semibold">{Translate[lang]?.days}</th>
									<th className="small fw-semibold">
										{Translate[lang]?.deducted_amount}
									</th>
								</tr>
							</thead>
							<tbody>
								{data.manualDeductionSummary.breakdown.map((m, i) => (
									<tr key={i} className="text-center">
										<td>
											<Badge bg="secondary" className="small">
												{m.type}
											</Badge>
										</td>
										<td className="small">{m.days ?? "-"}</td>
										<td className="small text-danger">
											{formatAmount(m.amount)}
										</td>
									</tr>
								))}
								<tr className="text-center" style={{ background: "#f8f9fa" }}>
									<td colSpan={2} className="fw-semibold">
										{Translate[lang]?.total_deducted_amount}
									</td>
									<td className="fw-bold text-danger">
										{formatAmount(
											data.manualDeductionSummary.totalDeductedAmount,
										)}
									</td>
								</tr>
							</tbody>
						</Table>
					</Card.Body>
				</Card>
			)}

			{/* All Deductions Breakdown */}
			<Card className="mb-3 shadow-sm">
				<Card.Header className="bg-white fw-semibold border-bottom">
					<i className="la la-list-alt me-2 text-danger" />
					{Translate[lang]?.deductions_breakdown}
				</Card.Header>
				<Card.Body className="p-0">
					<Table responsive className="mb-0">
						<thead>
							<tr className="text-center" style={{ background: "#f8f9fa" }}>
								<th className="small fw-semibold">{Translate[lang]?.type}</th>
								<th className="small fw-semibold">{Translate[lang]?.reason}</th>
								<th className="small fw-semibold">{Translate[lang]?.days}</th>
								<th className="small fw-semibold">
									{Translate[lang]?.deducted_amount}
								</th>
							</tr>
						</thead>
						<tbody>
							{data.deductions?.map((d, i) => (
								<tr key={i} className="text-center">
									<td>
										<Badge bg={deductionVariant(d.type)} className="small">
											{deductionTypeLabel(d.type)}
										</Badge>
									</td>
									<td className="small text-start px-3">{d.reason}</td>
									<td className="small">{d.days ?? "-"}</td>
									<td className="small text-danger fw-semibold">
										{formatAmount(d.amount)}
									</td>
								</tr>
							))}
						</tbody>
						<tfoot style={{ background: "#fff3cd" }}>
							<tr className="text-center">
								<td colSpan={3} className="fw-bold">
									{Translate[lang]?.total_deductions}
								</td>
								<td className="fw-bold text-danger">
									{formatAmount(data.totalDeductions)}
								</td>
							</tr>
						</tfoot>
					</Table>
				</Card.Body>
			</Card>

			{/* Final Salary Summary */}
			<Card className="shadow-sm mb-4">
				<Card.Header className="bg-white fw-semibold border-bottom">
					<i className="la la-check-circle me-2 text-success" />
					{Translate[lang]?.salary_summary}
				</Card.Header>
				<Card.Body className="p-0">
					<Table borderless responsive className="mb-0">
						<tbody>
							<tr>
								<td className="text-muted ps-3">
									{Translate[lang]?.basic_salary}
								</td>
								<td className="fw-semibold text-end pe-3">
									{formatAmount(data.baseSalary)}
								</td>
							</tr>
							<tr style={{ background: "#f8f9fa" }}>
								<td className="text-muted ps-3">
									{Translate[lang]?.total_deductions}
								</td>
								<td className="fw-semibold text-end pe-3 text-danger">
									- {formatAmount(data.totalDeductions)}
								</td>
							</tr>
							<tr>
								<td className="text-muted ps-3">
									{Translate[lang]?.calculated_payroll_salary}
								</td>
								<td className="fw-semibold text-end pe-3">
									{formatAmount(data.calculatedPayrollSalary)}
								</td>
							</tr>
							{data.isManuallyOverridden && (
								<tr style={{ background: "#fff3cd" }}>
									<td className="ps-3">
										{Translate[lang]?.manually_overridden}
										<Badge bg="warning" text="dark" className="ms-2 small">
											{Translate[lang]?.manually_overridden}
										</Badge>
									</td>
									<td className="fw-semibold text-end pe-3">
										{formatAmount(data.manualPayrollSalary)}
									</td>
								</tr>
							)}
							<tr style={{ background: "#e8f5e9" }}>
								<td className="ps-3 fw-bold fs-6">
									{Translate[lang]?.final_payroll_salary}
								</td>
								<td className="fw-bold text-end pe-3 text-success fs-6">
									{formatAmount(data.finalPayrollSalary)}
								</td>
							</tr>
						</tbody>
					</Table>
				</Card.Body>
			</Card>
		</div>
	);
};

export default PayrollDetails;
